import type { TripsFilterValues } from 'context/TripsViewContext'
import { areIntervalsOverlapping, endOfDay, isAfter, isBefore, isValid, isWithinInterval, parseISO, startOfDay } from 'date-fns'
import { maxTime, minTime } from 'date-fns/constants'
import type { DriverData } from 'screens/DriverManager/DriverManager.types'
import type { GeoPoint, LegData } from 'screens/TripDetails/TripDetails.types'
import { type VehicleData } from 'screens/VehicleManager/VehicleManager.types'
import type { BranchesData, StopsLocationGroup, TripData, TrackingInformation } from './TripDashboard.types'

export const getDataForDeleteTrip = (trip: TripData): TripData => trip

const shouldIgnoreDateFilter = (fromDate: Date | undefined, toDate: Date | undefined): boolean =>
  (!fromDate && !toDate) || Boolean(fromDate && toDate && !isAfter(toDate, fromDate))

const hasPickupAndDeliveryDates = (
  trip: Pick<TripData, 'earlyPickUpDate' | 'lateDeliveryDate'>,
): trip is { earlyPickUpDate: string, lateDeliveryDate: string } =>
  Boolean(trip.earlyPickUpDate && trip.lateDeliveryDate)

export const isTripMatchingDateFilter = (
  trip: Pick<TripData, 'earlyPickUpDate' | 'lateDeliveryDate'>,
  filters: Pick<TripsFilterValues, 'fromDate' | 'toDate'>,
): boolean => {
  const validRangeInterval = {
    start: new Date('2000-01-01'),
    end: new Date('2100-01-01'),
  }
  const fromDate = filters.fromDate
    && isValid(filters.fromDate)
    && isWithinInterval(filters.fromDate, validRangeInterval)
    ? startOfDay(filters.fromDate)
    : undefined
  const toDate = filters.toDate
    && isValid(filters.toDate)
    && isWithinInterval(filters.toDate, validRangeInterval)
    ? endOfDay(filters.toDate)
    : undefined

  if (shouldIgnoreDateFilter(fromDate, toDate)) {
    return true
  }
  if (!hasPickupAndDeliveryDates(trip)) {
    return false
  }
  const pickupDate = parseISO(trip.earlyPickUpDate)
  const deliveryDate = parseISO(trip.lateDeliveryDate)
  if (!isValid(pickupDate) || !isValid(deliveryDate)) {
    return false
  }

  // Some trips have pickup and delivery dates in reverse order.
  const pickupInterval = {
    start: startOfDay(isBefore(pickupDate, deliveryDate) ? pickupDate : deliveryDate),
    end: endOfDay(isAfter(deliveryDate, pickupDate) ? deliveryDate : pickupDate),
  }
  return areIntervalsOverlapping(pickupInterval, {
    start: fromDate ?? minTime,
    end: toDate ?? maxTime,
  }, { inclusive: true })
}

export const processDriversAndVehicles = (trip: TripData, vehicles: VehicleData[], drivers: DriverData[]): TripData => ({
  ...trip,
  assignedTruck: findVehicleByOriginalAndNormalizedLicensePlate(trip, vehicles, (trip) => trip.assignedTruckIdentifier),
  assignedTrailer: findVehicleByOriginalAndNormalizedLicensePlate(trip, vehicles, (trip) => trip.assignedTrailerIdentifier),
  assignedDriver: drivers.find((driver) => driver.cid === trip.assignedDriverId),
})

const NON_ALPHA_NUMERIC_REGEX = /[^\p{L}\p{N}]/gu

/**
 * Searches for a vehicle by its original and normalized license plate.
 *
 * First searches the whole list by the license plate.
 * Only if we have found no matches, then we must search for the normalized license plate.
 * This avoids the scenario in which we have a list of vehicles that contain both AA-11 and AA11 and the trip matches the wrong one.
 *
 * @param trip - The trip data containing the license plate information.
 * @param vehicles - The list of available vehicles to search within.
 *                   These vehicles may have original and normalized license plates.
 * @param tripLicensePlateSupplier - A function that provides the license plate identifier for the given trip.
 * @returns The found vehicle or undefined if not found.
 */
export const findVehicleByOriginalAndNormalizedLicensePlate = (
  trip: TripData,
  vehicles: VehicleData[],
  tripLicensePlateSupplier: (trip: TripData) => string | undefined
): VehicleData | undefined =>
    vehicles.find((vehicle) => vehicle.licensePlate === tripLicensePlateSupplier(trip))
    ?? vehicles.find((vehicle) => vehicle.licensePlate === normalizeLicensePlate(tripLicensePlateSupplier(trip) ?? ''))

export const normalizeLicensePlate = (licensePlate: string): string => {
  return licensePlate.replaceAll(NON_ALPHA_NUMERIC_REGEX, '')
    .toUpperCase()
}

export const processTrackingInformation = (trip: TripData, tripsTrackingInformation: TrackingInformation[]): TripData => ({
  ...trip,
  trackingInformation: tripsTrackingInformation.find((trackingInformation) => trackingInformation.tripCid === trip.entityId),
})

export const processBranches = (trips: TripData[]) => {
  const branches = trips
    .reduce((branches: BranchesData, trip: TripData) => {
      branches[trip.logicalSender] = trip.mappedSender
      return branches
    }, {})

  return Object.keys(branches)
    .sort((a, b) => a.localeCompare(b))
    .reduce((sorted, key) => {
      sorted[key] = branches[key]
      return sorted
    }, {})
}

export const groupStopsByLocation = (legs: LegData[]): StopsLocationGroup[] => {
  const groups = new Map<string, StopsLocationGroup>()

  legs.forEach(leg =>
    leg.wayPoints
      .filter(stop => stop.geoPoint && stop.type !== 'CUS')
      .forEach(stop => {
        const id = stop.partyIdentifier ?? getGeoPointId(stop.geoPoint!)
        const group = groups.get(id) ?? groups.set(id, {
          id,
          geoPoint: stop.geoPoint!,
          stopLegPairs: [],
        }).get(id)!

        group.stopLegPairs.push({ stop, leg })
      }),
  )
  return Array.from(groups.values())
}

export const getGeoPointId = (geoPoint: GeoPoint) => `${geoPoint.latitude.toFixed(5)}_${geoPoint.longitude.toFixed(5)}`
